import { create } from 'zustand';

const TOKEN_KEY = 'user_token';

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  setToken: (token: string | null) => void;
  signOut: () => void;
}

/**
 * Read the persisted token. Migrates a legacy `sessionStorage` token (from the
 * pre-localStorage build) into `localStorage` once, so a user who is already
 * signed in when this update ships isn't dropped back to the email-code flow.
 */
function loadToken(): string | null {
  const persisted = localStorage.getItem(TOKEN_KEY);
  const legacy = sessionStorage.getItem(TOKEN_KEY);
  // Always drop any legacy sessionStorage token so it can't be re-migrated
  // and silently re-authenticate after a sign-out.
  if (legacy) sessionStorage.removeItem(TOKEN_KEY);
  if (persisted) return persisted;
  if (legacy) {
    localStorage.setItem(TOKEN_KEY, legacy);
    return legacy;
  }
  return null;
}

/**
 * Token auth, persisted in localStorage so the session survives new tabs,
 * windows, and browser restarts — re-authenticating is an email one-time-code
 * round-trip, so losing the session per tab (the old sessionStorage behaviour)
 * was too disruptive. Cleared on sign-out or a 401. The API fetcher reads
 * `token`; a 401 calls `signOut()`.
 */
export const useAuth = create<AuthState>((set) => {
  const initial = loadToken();
  return {
    token: initial,
    isAuthenticated: Boolean(initial),
    setToken: (token) => {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
      set({ token, isAuthenticated: Boolean(token) });
    },
    signOut: () => {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY); // also drop any pre-migration token
      set({ token: null, isAuthenticated: false });
    },
  };
});

// Keep open tabs in sync: a `storage` event fires in OTHER tabs when the token
// changes, so a sign-out / 401 in one tab logs the others out too (and a
// sign-in propagates). The event doesn't fire in the tab that made the change.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === TOKEN_KEY) {
      useAuth.setState({ token: event.newValue, isAuthenticated: Boolean(event.newValue) });
    }
  });
}
